import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getPaddleEnvironment } from "@/lib/paddle";

export interface SubscriptionRow {
  id: string;
  paddle_subscription_id: string;
  paddle_customer_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
  quantity: number;
}

function isAccessActive(s: SubscriptionRow | null): boolean {
  if (!s) return false;
  const endOk = !s.current_period_end || new Date(s.current_period_end) > new Date();
  if (s.status === 'active' || s.status === 'trialing' || s.status === 'past_due') return endOk;
  if (s.status === 'canceled') return !!s.current_period_end && new Date(s.current_period_end) > new Date();
  return false;
}

export function useSubscription() {
  const { userId, mode, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSub = useCallback(async () => {
    if (!isAuthenticated || mode !== 'real' || !userId) {
      setSubscription(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    const { data } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('environment', getPaddleEnvironment())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const row = (data as SubscriptionRow | null) ?? null;
    setSubscription(row);
    setLoading(false);
    return row;
  }, [userId, mode, isAuthenticated]);

  useEffect(() => { fetchSub(); }, [fetchSub]);

  useEffect(() => {
    if (!userId || mode !== 'real') return;
    const ch = supabase
      .channel(`sub_${userId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'subscriptions',
        filter: `user_id=eq.${userId}`,
      }, () => fetchSub())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId, mode, fetchSub]);

  const isActive = isAccessActive(subscription);
  const isTrialing = subscription?.status === 'trialing' && isActive;
  const isPastDue = subscription?.status === 'past_due';
  const isCanceled = subscription?.status === 'canceled';
  // Demo mode is treated as a guided tour — paywall doesn't apply.
  const isDemo = mode === 'demo';
  const hasAccess = isDemo || isActive;

  return {
    subscription,
    loading,
    isActive,
    isTrialing,
    isPastDue,
    isCanceled,
    hasAccess,
    refresh: fetchSub,
  };
}